import {InvalidTokenError} from '@packages/utils';
import {useInfiniteQuery} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import {
  type GetEntryManagementsReturnValueType,
  getEntryManagements,
} from '../../../core/repositories/mypageEntryManagementsRepository';
import {useIsSessionExpired} from '../../../core/usecases/auth/useIsSessionExpired';
import type {OverallStatusType, SortKeyType, SortOrderType} from '../constants';

/** SP版のページングで1回に取得する件数 */
export const DEFAULT_PAGE_LIMIT = 20;

type UsePaginatedEntriesOptions = {
  sortBy?: SortKeyType;
  sortOrder?: SortOrderType;
  overallStatus?: OverallStatusType | OverallStatusType[];
  hasCompletedEntry?: boolean;
  hasLatestStep?: boolean;
  companyName?: string;
  /** ページングを有効にするか（true: 20件ずつ取得、false: 全件取得） */
  isPagingEnabled?: boolean;
  enabled?: boolean;
  /** データをfreshとみなす時間（ミリ秒）。この間はキャッシュを使用しリクエストしない */
  staleTime?: number;
};

/**
 * エントリー管理一覧の取得フック
 *
 * @description
 * - isPagingEnabled=true: 20件ずつページング取得
 * - isPagingEnabled=false: 全件取得
 */
export const usePaginatedEntries = ({
  sortBy,
  sortOrder,
  overallStatus,
  hasCompletedEntry,
  hasLatestStep,
  companyName,
  isPagingEnabled = false,
  enabled = true,
  staleTime,
}: UsePaginatedEntriesOptions = {}) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  const query = useInfiniteQuery<
    GetEntryManagementsReturnValueType,
    Error,
    GetEntryManagementsReturnValueType,
    (string | boolean | OverallStatusType | OverallStatusType[] | undefined)[]
  >({
    queryKey: [
      'paginatedEntryManagements',
      sortBy,
      sortOrder,
      overallStatus,
      hasCompletedEntry,
      hasLatestStep,
      companyName,
      isPagingEnabled,
    ],
    enabled,
    staleTime,
    queryFn: async ({pageParam = 1}) => {
      const cookies = parseCookies();

      const result = await getEntryManagements({
        cookies,
        sortBy,
        sortOrder,
        overallStatus,
        hasCompletedEntry,
        hasLatestStep,
        companyName,
        ...(isPagingEnabled && {
          page: pageParam as number,
          limit: DEFAULT_PAGE_LIMIT,
        }),
      });

      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!isPagingEnabled || !lastPage.ok) {
        return undefined;
      }

      const {entries, totalCount, page} = lastPage.value;

      if (typeof page !== 'number' || page < 1 || entries.length === 0) {
        return undefined;
      }

      const currentCount = allPages.reduce((acc, p) => {
        if (p.ok) {
          return acc + p.value.entries.length;
        }
        return acc;
      }, 0);

      return currentCount < totalCount ? page + 1 : undefined;
    },
  });

  return query;
};
